import React from 'react';
import {
  Flex,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  IconButton,
  HStack,
} from '@chakra-ui/react';
import {
  CheckIcon,
  CloseIcon,
  EditIcon,
  DeleteIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MinusIcon,
} from '@chakra-ui/icons';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import Card from '../card/Card';

const columnHelper = createColumnHelper();

export default function DynamicTable({
  columnsData,
  data,
  page,
  setPage,
  totalPages,
  showRowInfo,
  rowClickPath, // Nueva prop para personalizar la navegación
  onEdit,
  onDelete,
  // Acción opcional "BAJA" (distinta a "ELIMINAR"). Se usa principalmente en
  // Inscripciones. Si se pasa, se renderiza un botón adicional en la columna
  // de acciones. No afecta el comportamiento existente de onEdit/onDelete.
  onDischarge,
}) {
  const navigate = useNavigate();
  const [sorting, setSorting] = React.useState([]);
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const columns = React.useMemo(() => {
    const baseColumns = columnsData.map((col) =>
      columnHelper.accessor(col.accessor, {
        id: col.accessor,
        header: () => (
          <Text fontSize={{ sm: '10px', lg: '12px' }}>{col.Header}</Text>
        ),
        cell: (info) => {
          const rowData = info.row.original;

          // Si la columna define un Cell personalizado (como en columnas
          // con formateo de fechas, estados calculados o acciones), lo
          // respetamos y no aplicamos el render por defecto.
          if (typeof col.Cell === 'function') {
            return (
              <Flex
                align="center"
                onClick={(e) => e.stopPropagation()}
              >
                {col.Cell({
                  value: info.getValue(),
                  row: { original: rowData },
                })}
              </Flex>
            );
          }

          const value = col.computedValue
            ? col.computedValue(rowData)
            : info.getValue();

          let displayValue;
          if (typeof value === 'boolean') {
            displayValue = value ? (
              <CheckIcon color="green.500" />
            ) : (
              <CloseIcon color="red.500" />
            );
          } else {
            displayValue = (
              <Text fontSize="sm" fontWeight="700">
                {value || '-'}
              </Text>
            );
          }

          return <Flex align="center">{displayValue}</Flex>;
        },
      })
    );

    // Agregar columna de acciones si onEdit, onDelete o onDischarge están disponibles
    if (onEdit || onDelete || onDischarge) {
      baseColumns.push(
        columnHelper.accessor('actions', {
          id: 'actions',
          header: () => (
            <Text fontSize={{ sm: '10px', lg: '12px' }}>ACCIONES</Text>
          ),
          cell: (info) => {
            const rowData = info.row.original;
            return (
              <Flex align="center" gap={2}>
                {onEdit && (
                  <IconButton
                    aria-label="Editar"
                    icon={<EditIcon />}
                    size="sm"
                    variant="ghost"
                    colorScheme="blue"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(rowData);
                    }}
                  />
                )}
                {onDischarge && (
                  <IconButton
                    aria-label="Dar de baja"
                    title="Baja"
                    icon={<MinusIcon />}
                    size="sm"
                    variant="ghost"
                    colorScheme="orange"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDischarge(rowData);
                    }}
                  />
                )}
                {onDelete && (
                  <IconButton
                    aria-label="Eliminar"
                    title="Eliminar"
                    icon={<DeleteIcon />}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(rowData);
                    }}
                  />
                )}
              </Flex>
            );
          },
        })
      );
    }

    return baseColumns;
  }, [columnsData, onEdit, onDelete, onDischarge]);

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      // Scroll horizontal en TODAS las resoluciones: antes era 'hidden' en
      // lg+ y algunas grillas (ej. Dictados con acciones al final) dejaban
      // columnas fuera del viewport sin forma de acceder a ellas.
      overflowX="auto"
    >
      <Box>
        <Table variant="simple" color="gray.500" mb="24px" mt="12px">
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th key={header.id} borderColor={borderColor} pe="10px">
                    <Flex
                      justify="space-between"
                      align="center"
                      fontSize={{ sm: '10px', lg: '12px' }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </Flex>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Tr
                key={row.id}
                cursor="pointer"
                _hover={{ bg: 'gray.100' }}
                onClick={
                  showRowInfo
                    ? () => {
                        const path = rowClickPath 
                          ? rowClickPath.replace(':id', row.original.id)
                          : `/admin/default/${row.original.id}`;
                        navigate(path);
                      }
                    : undefined
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <Td key={cell.id} borderColor="transparent">
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext()
                    )}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* Paginación */}
      {totalPages > 1 && (
        <Flex justify="center" align="center" mt={4} mb={4}>
          <HStack spacing={2}>
            <IconButton
              aria-label="Página anterior"
              icon={<ChevronLeftIcon />}
              isDisabled={page === 0}
              onClick={() => setPage(page - 1)}
              size="sm"
            />
            <Text fontSize="sm" mx={4}>
              Página {page + 1} de {totalPages}
            </Text>
            <IconButton
              aria-label="Página siguiente"
              icon={<ChevronRightIcon />}
              isDisabled={page + 1 >= totalPages}
              onClick={() => setPage(page + 1)}
              size="sm"
            />
          </HStack>
        </Flex>
      )}
    </Card>
  );
}
